// src/auth/vectrosApiTokenCache.ts
var EMPTY_RESOLVED_SCOPE = { allowedActions: [], identity: {} };
function parseResolvedScope(raw) {
  if (raw == null || typeof raw !== "object") return EMPTY_RESOLVED_SCOPE;
  const obj = raw;
  const allowedActions = Array.isArray(obj.allowedActions) ? obj.allowedActions.filter((a) => typeof a === "string") : [];
  let identity = {};
  if (obj.identity != null && typeof obj.identity === "object" && !Array.isArray(obj.identity)) {
    identity = {};
    for (const [k, v] of Object.entries(obj.identity)) {
      if (typeof v === "string") identity[k] = v;
    }
  }
  return { allowedActions, identity };
}
var REFRESH_BEFORE_MS = 6e4;
var SHARED_MINT_RETRY_DELAY_MS = 1500;
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
var cachedTokens = /* @__PURE__ */ new Map();
var cachedExpiriesMs = /* @__PURE__ */ new Map();
var cachedResolvedScopes = /* @__PURE__ */ new Map();
var inFlightMints = /* @__PURE__ */ new Map();
var cacheGeneration = 0;
var minter = null;
var assumer = null;
function slotKey(tenantId, contextId, override) {
  const base = `${tenantId}|${contextId ?? ""}`;
  return override ? `${base}|${override.namespace}|${override.value}` : base;
}
function setPartnerApiTokenMinter(source) {
  minter = source;
}
function setPartnerApiTokenAssumer(source) {
  assumer = source;
}
function getVectrosApiToken(tenantId, contextId, identityOverride) {
  if (!tenantId) {
    return Promise.reject(new Error("getVectrosApiToken: a tenantId is required"));
  }
  const key = slotKey(tenantId, contextId, identityOverride);
  const now = Date.now();
  const cachedToken = cachedTokens.get(key);
  if (cachedToken && (cachedExpiriesMs.get(key) ?? 0) > now + REFRESH_BEFORE_MS) {
    return Promise.resolve(cachedToken);
  }
  const inFlight = inFlightMints.get(key);
  if (inFlight) {
    return inFlight;
  }
  const generationAtStart = cacheGeneration;
  const fetchFresh = () => {
    if (!identityOverride) {
      if (!minter) {
        throw new Error(
          "vectrosApiTokenCache: Vectros API token minter not registered. Call setPartnerApiTokenMinter() at app boot before any Vectros API call."
        );
      }
      return minter(tenantId, contextId);
    }
    if (!assumer) {
      throw new Error(
        "vectrosApiTokenCache: Vectros API token assumer not registered. Call setPartnerApiTokenAssumer() at app boot before requesting an identity override."
      );
    }
    return getVectrosApiToken(tenantId, contextId).then((baseBearer) => assumer(baseBearer, identityOverride));
  };
  let mintPromise;
  mintPromise = (async () => {
    await Promise.resolve();
    try {
      let result;
      try {
        result = await fetchFresh();
      } catch (firstErr) {
        const stillUnconfigured = identityOverride ? !assumer : !minter;
        if (stillUnconfigured || cacheGeneration !== generationAtStart) {
          throw firstErr;
        }
        await delay(SHARED_MINT_RETRY_DELAY_MS);
        if (cacheGeneration !== generationAtStart) {
          throw firstErr;
        }
        result = await fetchFresh();
      }
      const { token, expiresAtMs, resolvedScope } = result;
      if (cacheGeneration !== generationAtStart) {
        throw new Error("Session cleared during Vectros API token mint; discarding result.");
      }
      cachedTokens.set(key, token);
      cachedExpiriesMs.set(key, expiresAtMs);
      if (resolvedScope) {
        cachedResolvedScopes.set(key, resolvedScope);
      } else {
        cachedResolvedScopes.delete(key);
        console.warn(
          "vectrosApiTokenCache: mint succeeded but the response carried no resolvedScope \u2014 useScopeGate will see an empty allowedActions/identity. Either the minter/backend hasn't been updated to supply it yet, or a deploy landed out of order."
        );
      }
      return token;
    } finally {
      if (inFlightMints.get(key) === mintPromise) {
        inFlightMints.delete(key);
      }
    }
  })();
  inFlightMints.set(key, mintPromise);
  return mintPromise;
}
function getVectrosResolvedScope(tenantId, contextId, identityOverride) {
  const key = slotKey(tenantId, contextId, identityOverride);
  return getVectrosApiToken(tenantId, contextId, identityOverride).then(
    () => cachedResolvedScopes.get(key) ?? null
  );
}
function clearVectrosApiTokenCache() {
  cacheGeneration += 1;
  cachedTokens.clear();
  cachedExpiriesMs.clear();
  cachedResolvedScopes.clear();
  inFlightMints.clear();
}
function __resetVectrosApiTokenCacheForTest() {
  clearVectrosApiTokenCache();
  minter = null;
  assumer = null;
}

// src/auth/errors.ts
var AuthError = class _AuthError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
    Object.setPrototypeOf(this, _AuthError.prototype);
  }
  code;
  name = "AuthError";
};
function isAuthError(e) {
  return e instanceof AuthError;
}

export { AuthError, __resetVectrosApiTokenCacheForTest, clearVectrosApiTokenCache, getVectrosApiToken, getVectrosResolvedScope, isAuthError, parseResolvedScope, setPartnerApiTokenAssumer, setPartnerApiTokenMinter };
//# sourceMappingURL=chunk-26O3XQW6.mjs.map
//# sourceMappingURL=chunk-26O3XQW6.mjs.map